import { openExternalUrl } from "../../utils/native/OpenExternalUrl";
import { isNativeApp, getAppType, AppType } from "../../utils/native/nativeHelpers";
import { getKeys } from "../../utils/helpers";
import { getServerDate } from "../../utils/storage/date";
import { extend, hashToArray } from "../../utils/vanilla";
import Notifications from "../../utils/notifications";
import { CancelablePromise } from "../../utils/cancelablePromise";
import { BaseCache } from "../../utils/storage/cache";
import { MESSAGE_LIMIT } from "../../sync/messages";
import { SyncType } from "../../sync/sync.types";

import Message, { IMessage, IMessageContent } from "../../model/Message";
import { NOUSERID, NODEVICEID } from "../../model/DutyStatusLog";

import { DriveStore } from "../../store/reducers";
import { MobileActions } from "../../store/mobile/actions";
import Messages from "../../model/Messages";
import Api from "../../services/api";

export const enum ViewTypes {
    Device = "device",
    Recipient = "recipient",
    System = "system"
}

export interface IGroup {
    date: string;
    messages: Message[];
}

export function locationLinkClick(e) {
    e.preventDefault();
    let uri;
    if (e.currentTarget.className === "locationRoute") {
        uri = e.currentTarget.href;
        let stops = Array.prototype.slice.call(document.getElementsByClassName("routeItem")).reduce((indexedStops, stop) => {
            indexedStops[stop.id] = stop;
            return indexedStops;
        }, {});
        if (e.currentTarget.dataset.stop && stops[e.currentTarget.dataset.stop].classList.length === 1) {
            stops[e.currentTarget.dataset.stop].classList.toggle("read");
        }
    } else {
        uri = e.currentTarget.href;
    }

    if (isNativeApp()) {
        e.preventDefault();
        if (window.device.platform === "Android" && window.device.manufacturer === "Garmin" && window.garminClickLocationPlugin) {
            window.garminClickLocationPlugin.handleCoordClick(uri);
            return undefined;
        }

        if (getAppType() === AppType.CordovaApp && window.device.platform === "Android") {
            navigator.app.loadUrl(uri, { openExternal: true });
        } else {
            window.open(uri, "_system");
        }
    } else {
        window.open(uri);
    }
    return false;
}

export function messageLinkClick(e) {
    let uri = e.currentTarget.href;
    openExternalUrl(uri, "_blank");
}

export function isSystemMessage(message: IMessage) {
    return typeof (message.user) === "undefined" || message.user?.id === NOUSERID;
}

export function getMessagesDictionaryFromStore(store: DriveStore, userId: string): IIdDictionary<IMessage> {
    let storedMessages: DeepReadonlyObject<IIdDictionary<IMessage>> = store.getState().messages[userId] || {};
    let messages: IIdDictionary<IMessage> = {};
    for (let id of getKeys(storedMessages)) {
        messages[id] = new Message(extend(true, {}, storedMessages[id]) as IMessage);
    }
    return messages;
}

export function isCurrentDeviceMessage(msg: Message, currentVehicle: IIdEntity | null) {
    return msg.device?.id !== NODEVICEID && msg.device?.id === currentVehicle?.id;
}


export const clearUnReadNotifications = (allMessages: Message[], store: DriveStore, currentUser: IUser) => {
    if (allMessages.length > 0) {
        const isMsgUnRead = (msg: Message) => !msg.read && (typeof (msg.user) === "undefined" || (msg.user?.id !== NOUSERID && msg.user.id !== currentUser?.id));
        let hasUnreadMessages = allMessages.some(isMsgUnRead);
        if (!hasUnreadMessages && store.getState().mobile.newMessage) {
            currentUser && Notifications.remove("unreadMessages", currentUser?.id);
            store.dispatch(MobileActions.setNewMessage(hasUnreadMessages));
        }
    }
};

export const populateRoutes = (deviceMessages: Message[], store: DriveStore, api: Api) => {
    let apiUser = api.getUser();
    if (!apiUser) {
        return;
    }
    let routes = store.getState().routes[apiUser.id] || {};
    if (!routes) {
        return;
    }
    deviceMessages.forEach(message => {
        let content = message.messageContent;
        if (content?.routeId && routes && routes.hasOwnProperty(content.routeId)) {
            message._routeName = routes[content.routeId].name;
        }
    });
};

export const getMissingUsersInUserCache = (recipientChatsDict: IIdDictionary<Message[]>, users: IIdDictionary<IUser>, currentUser: IUser) => {
    let missingUsers = {};
    Object.keys(recipientChatsDict).forEach(key => {
        if (recipientChatsDict[key].length > 0) {
            let latestRecipientChat = recipientChatsDict[key][0];
            let user = latestRecipientChat.user;
            let recipient = latestRecipientChat.recipient;

            if (user && user.id !== NOUSERID) {
                if (user.id === currentUser?.id && recipient && recipient.id !== NOUSERID && !users[recipient.id]) {
                    missingUsers[recipient.id] = recipient.id;
                } else if (!users[user.id]) {
                    missingUsers[user.id] = user.id;
                }
            }
        }
    });
    return missingUsers;
};

export const updateMissingUsers = (missingUsers: {}, api: Api, userCache: BaseCache<IUser>) => {
    return userCache.getOrFetch(api, hashToArray<IIdEntity>(missingUsers).map(userId => ({ id: userId })));
};

export const updateSenderNames = (
    recipientChatsDict: IIdDictionary<Message[]>,
    latestRecipientChats: Message[],
    users: IIdDictionary<IUser>,
    currentUser: IUser,
    defaultSenderName: string
) => {
    // populate admin name of each latest recipient conversation
    Object.keys(recipientChatsDict).forEach(key => {
        if (recipientChatsDict[key].length > 0) {
            let latestRecipientChat = recipientChatsDict[key][0];
            let user = latestRecipientChat.user;
            let recipient = latestRecipientChat.recipient;

            // Message that user sent to themselves should show up as conversation with admin
            if (key === currentUser?.id && recipient && recipient.id !== NOUSERID && key === recipient.id) {
                latestRecipientChat._userName = defaultSenderName;
                latestRecipientChats.push(latestRecipientChat);
                return;
            }
            if (user?.id !== NOUSERID) {
                let senderId = user?.id;
                if (senderId === currentUser?.id && recipient?.id !== NOUSERID) {
                    senderId = recipient?.id;
                }
                let fetchedUser = senderId && users[senderId];
                let fetchedUserName = defaultSenderName;
                if (fetchedUser) {
                    fetchedUserName = `${ fetchedUser.firstName } ${ fetchedUser.lastName }`;
                }

                latestRecipientChat._userName = fetchedUserName;
                let foundIndex = latestRecipientChats.indexOf(latestRecipientChat);
                if (foundIndex !== -1) {
                    latestRecipientChats[foundIndex] = latestRecipientChat;
                } else {
                    latestRecipientChats.push(latestRecipientChat);
                }
            }
        }
    });
};

export const groupRecipientConversations = (allRecipientMessages: Message[], currentUser: IUser) => {
    let recipientChatsDict: IIdDictionary<Message[]> = {};
    const isRecipientLoggedInUser = (recipient) => {
        return recipient && recipient?.id === currentUser?.id;
    };
    const isLoggedInUserSender = (user) => {
        return user && user?.id === currentUser?.id;
    };
    allRecipientMessages.forEach(message => {
        let recipient = message.recipient;
        let user = message.user;
        if (isRecipientLoggedInUser(recipient) && user && user?.id !== NOUSERID) {
            if (!recipientChatsDict[user.id]) {
                recipientChatsDict[user.id] = [];
            }
            recipientChatsDict[user.id].push(message);
        }
        if (isLoggedInUserSender(user) && recipient && recipient?.id !== NOUSERID) {
            if (!recipientChatsDict[recipient.id]) {
                recipientChatsDict[recipient.id] = [];
            }
            recipientChatsDict[recipient.id].push(message);
        }
    });
    return recipientChatsDict;
};

export const getGroupedMessagesAddressesLookup = (deviceMessages: Message[]) => {
    return deviceMessages.reduce((messageDict, message) => {
        if (message._groupedMessages) {
            let addresses = message._groupedMessages.map(m => m.messageContent?.address || "");
            if (addresses.length > 3) {
                addresses = [...addresses.slice(0, 1), "...", ...addresses.slice(-1)];
            }
            messageDict[message.id] = addresses;
        }
        return messageDict;
    }, {} as IIdDictionary<string[]>);
};

const populateRouteInfo = (mes: Message, store: DriveStore, currentUser: IUser, messagesConstructor: Messages) => {
    if (!currentUser?.id) {
        return;
    }
    let content = mes.messageContent;
    let routeId = content?.routeId;
    if (routeId) {
        let routes = store.getState().routes[currentUser?.id] || {};
        let routeInfo = messagesConstructor.getRelatedRouteMessages(mes).map(m => m.messageContent) as IMessageContent[];
        let routeName = routes && routes.hasOwnProperty(routeId) ? routes[routeId].name : undefined;
        mes._routeInfo = routeInfo;
        mes._routeName = routeName;
    }
};

export const groupMessagesByDate = (msgs: Message[]) => {
    const messageGroups = msgs.reduce((groups: IGroup[], message: Message) => {
        const sentDate = message.getSentDate();
        if (sentDate) {
            if (!groups[sentDate]) {
                groups[sentDate] = [];
            }
            groups[sentDate].push(message);
        }
        return groups;
    }, new Array<IGroup>());

    return Object.keys(messageGroups).map((date) => {
        return { date: date, messages: messageGroups[date] } as IGroup;
    });
};

export const markMessagesAsRead = (msgs: Message[], deviceId: string, currentUser: IUser, messagesConstructor: Messages) => {
    let counter = 0;
    const sync = window.driveApp.sync;
    msgs.forEach(message => {
        let content = message.messageContent;
        let sender = message.user;
        let messageSenderId = sender?.id !== NOUSERID && sender?.id;
        let isDirectionToVehicle = message.isDirectionToVehicle;
        let device = message.device;

        let isAdminSameAsLoggedInUser = messageSenderId === currentUser?.id;
        let isIncomingCurrentDeviceMsg = isDirectionToVehicle && device?.id !== NODEVICEID && device?.id === deviceId;

        // Mark messages that are sent from admin (different from current user), system messages and device messages where "admin" and "driver" are the same as "read"
        if ((messageSenderId !== currentUser?.id) || sender === null || (isIncomingCurrentDeviceMsg && isAdminSameAsLoggedInUser) && counter < MESSAGE_LIMIT) {
            if (content?.routeId) {
                let routeMessages = messagesConstructor.getMessages(undefined).filter(routeMsg => {
                    return routeMsg.messageContent?.routeId && !routeMsg.read && routeMsg.delivered && routeMsg.messageContent.routeId === content?.routeId;
                });

                // Only tick the counter once per route message.
                if (routeMessages.length) {
                    counter++;
                }

                routeMessages.forEach(routeMsg => {
                    routeMsg.read = getServerDate().toISOString();
                    routeMsg.markedReadBy = { id: currentUser?.id };
                    void sync.messages.setItem(routeMsg, true);
                });
            } else if (!message.read && message.delivered) {
                message.read = getServerDate().toISOString();
                message.markedReadBy = { id: currentUser?.id };
                void sync.messages.setItem(message, true);
                counter++;
            }
        }
    });
    if (counter) {
        // If we"ve marked messages as read, update the unread messages notification if it exists (it should).
        let currentMessageNotifications = Notifications.get("unreadMessages", currentUser?.id);
        if (currentMessageNotifications !== null && currentMessageNotifications.count) {
            Notifications.updateUnreadMessagesNotification(currentMessageNotifications.count - counter, currentUser?.id);
        }

        // Sync can happen later
        void sync.synchronize(false, null, [SyncType.Messages]);
    }
};

export const filterMessages = (
    msgs: Message[],
    store: DriveStore,
    deviceId,
    currentUser: IUser,
    viewType: string,
    messagesConstructor: Messages,
    users: IIdDictionary<IUser>,
    senderId: string,
    setViewTitle: React.Dispatch<React.SetStateAction<string>>,
    defaultSenderName: string
 ) => {

    let filteredMessages: Message[] = [];
    const isValidDeviceMessage = (msg: Message) => {
        return msg.device?.id !== NODEVICEID && msg.device?.id === deviceId;
    };

    if (viewType === ViewTypes.Device) {

        let deviceMessages = msgs.filter(msg => {
            let messageContent = msg.messageContent;
            return isValidDeviceMessage(msg) && messageContent && !isSystemMessage(msg);
        });

        deviceMessages.forEach((msg) => {
            populateRouteInfo(msg, store, currentUser, messagesConstructor);
            filteredMessages.push(msg);
        });
    } else if (viewType === ViewTypes.Recipient) {

        msgs.forEach((item) => {
            let sender = item.user;
            let recipient = item.recipient;
            if (recipient?.id !== NOUSERID && sender?.id !== NOUSERID) {

                // Get all incoming and outgoing messages between sender and the recipient (i.e current user).
                if ((sender?.id === senderId && recipient?.id === currentUser?.id) || (recipient?.id === senderId && sender?.id === currentUser?.id)) {
                    populateRouteInfo(item, store, currentUser, messagesConstructor);
                    filteredMessages.push(item);
                }
            }
        });

        // Show viewTitle as Administrator for message that user sent to themselves
        if (users.hasOwnProperty(senderId) && senderId !== currentUser?.id) {
            setViewTitle(`${ users[senderId].firstName } ${ users[senderId].lastName }`);
        } else {
            setViewTitle(defaultSenderName);
        }

    } else {

        let deviceMessages = msgs.filter(isValidDeviceMessage);
        deviceMessages.forEach((item) => {
            if (isSystemMessage(item)) {
                filteredMessages.push(item);
            }
        });
    }
    return filteredMessages;
};

export const populateMessageLocationUri = (mes: Message, filteredMessages: Message[], routeInfoUriPromises: CancelablePromise<void>[]) => {
    let messageLocationUri: string[] = [];
    let content = mes.messageContent;

    let routeId = content?.routeId;

    let setMessageLocationUri = (uri: string, item: Message) => {
        let foundIndex = filteredMessages.indexOf(item);
        if (foundIndex !== -1) {
            messageLocationUri.push(uri);
            item._messageLocationUri = messageLocationUri;
            filteredMessages[foundIndex] = item;
        }
    };

    if (routeId) {
        let stopAddress = new Message();
        let routeInfo = mes._routeInfo;
        routeInfo?.forEach(route => {
            stopAddress.messageContent = route;
            routeInfoUriPromises.push(stopAddress.getAddressUri().then(uri => {
                setMessageLocationUri(uri, mes);
            }));
        });
    } else {
        if (content?.contentType === "Location" && (content.latitude || content.longitude)) {
            routeInfoUriPromises.push(mes.getAddressUri().then(uri => {
                setMessageLocationUri(uri, mes);
            }));
        }
    }
};

export const toggleButtons = (targetId: string, disable: boolean) => {
    let cannedResponseElement = targetId && document.getElementById(`ul#${ targetId }`) as HTMLElement;
    if (cannedResponseElement) {
        let cannedReponseOptions = cannedResponseElement.getElementsByTagName("button");
        // Only show the selected canned response option and disable remaining options
        for (let i = 0; i < cannedReponseOptions.length; i++) {
            cannedReponseOptions[i].disabled = disable;
        }
    }
};
